import { createContext, useContext, useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Roles } from './roles'

/**
 * The authenticated identity shape returned by `GET /api/v1/auth/me`.
 *
 * Mirrors the backend `MeResponse` contract. `roles` is always present
 * (defaults to `[]`), so admin-only surfaces key off it without optional
 * chaining — important under `exactOptionalPropertyTypes`.
 */
export type Me = {
  userId: string
  email: string
  displayName: string
  emailConfirmed: boolean
  organizationId: string
  locale: string
  roles: string[]
}

type MeContextValue = {
  me: Me
  isAdmin: boolean
}

/**
 * Holds the resolved identity for the authenticated app shell. `null` until a
 * `MeProvider` is mounted — `useMe` throws in that case so a missing provider
 * is a loud, immediate error rather than a silent undefined.
 */
const MeContext = createContext<MeContextValue | null>(null)

/**
 * Publishes the resolved identity (and the derived `isAdmin` flag) to the
 * authenticated subtree. Rendered by the `_app` layout only once the
 * `['auth','me']` query has resolved, so `me` is always a concrete value here
 * and children never have to handle a loading/undefined identity.
 */
export function MeProvider({
  me,
  children,
}: {
  me: Me
  children: React.ReactNode
}) {
  const value = useMemo<MeContextValue>(
    () => ({ me, isAdmin: me.roles.includes(Roles.Admin) }),
    [me],
  )
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>
}

/**
 * Reads the current identity from context. Throws if called outside a
 * `MeProvider` (i.e. outside the authenticated shell).
 */
export function useMe(): MeContextValue {
  const ctx = useContext(MeContext)
  if (ctx === null) {
    throw new Error('useMe must be used within a <MeProvider>')
  }
  return ctx
}

/**
 * Route guard for admin-only surfaces: redirects to `/dashboard` when the
 * current user is not an admin. Returns `isAdmin` so the calling component can
 * render nothing during the redirect frame. Call from an admin route's
 * `component` body.
 */
export function useRequireAdmin(): boolean {
  const { isAdmin } = useMe()
  const navigate = useNavigate()
  useEffect(() => {
    if (!isAdmin) {
      void navigate({ to: '/dashboard' })
    }
  }, [isAdmin, navigate])
  return isAdmin
}
